import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/cn";
import { listProviders, type ProviderConfig } from "@/api/providers";
import { useClientPagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/pagination-bar";
import { DiscoveryProfileEditor } from "@/components/discovery/discovery-profile-editor";
import {
  useDiscoveryProfile,
  useSaveDiscoveryProfile,
  enabledCompanyCount,
  enabledProviderCount,
} from "@/hooks/useDiscovery";
import {
  TYPE_LABELS,
  TYPE_COLORS,
  TYPE_FILTER_TABS,
  filterProviders,
  isSelectableSignalSource,
  type ProviderTypeFilter,
} from "@/lib/provider-ui";
import { JobDiscoveryPage } from "@/pages/job-discovery.page";

type PageTab = "sources" | "providers" | "discover";

function selectedSignalSources(
  profile: { signalSources?: string[] } | null | undefined,
): string[] {
  return profile?.signalSources ?? [];
}

function ProvidersPanel() {
  const { data: profile } = useDiscoveryProfile();
  const saveMutation = useSaveDiscoveryProfile();
  const [filterTab, setFilterTab] = useState<ProviderTypeFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedProvider, setExpandedProvider] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const selectedIds = new Set(selectedSignalSources(profile));

  const {
    data: providers = [],
    isLoading,
    error,
  } = useQuery<ProviderConfig[]>({
    queryKey: ["providers"],
    queryFn: listProviders,
    staleTime: 30_000,
  });

  const filteredProviders = filterProviders(providers, {
    search: searchQuery,
    type: filterTab,
  });

  const {
    pageItems: pagedProviders,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    showingFrom,
    showingTo,
  } = useClientPagination(filteredProviders, [filterTab, searchQuery]);

  async function toggleSource(providerId: string) {
    setMessage(null);
    const current = selectedSignalSources(profile);
    const next = selectedIds.has(providerId)
      ? current.filter((id) => id !== providerId)
      : [...current, providerId];

    try {
      await saveMutation.mutateAsync({ signalSources: next });
      setMessage("Saved your signal source selections.");
    } catch (err) {
      setMessage(
        err instanceof Error ? err.message : "Failed to save signal sources.",
      );
    }
  }

  return (
    <div>
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Signal sources ({enabledProviderCount(profile)} selected)
          </h2>
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search providers…"
              className="w-48 rounded-md border border-border bg-bg py-1.5 pl-3 pr-3 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>
        </div>
        <div className="flex gap-1">
          {TYPE_FILTER_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setFilterTab(tab)}
              className={cn(
                "rounded-md px-3 py-1.5 text-xs font-medium transition",
                filterTab === tab
                  ? "bg-brand text-brand-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {tab === "all" ? "All" : TYPE_LABELS[tab]}
            </button>
          ))}
        </div>
      </div>

      <p className="mb-4 text-xs text-muted-foreground">
        Choose hiring boards and market-signal providers for discovery scan and
        Signals refresh. ATS careers are configured per account on the Sources
        tab. Providers without a runtime adapter cannot be selected.
      </p>

      {message && (
        <p className="mb-4 text-xs text-muted-foreground">{message}</p>
      )}

      {isLoading && (
        <div className="flex items-center justify-center rounded-lg border border-border bg-card p-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      )}

      {error && !isLoading && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Failed to load providers.
        </div>
      )}

      {!isLoading && !error && filteredProviders.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          No providers match your filters.
        </div>
      )}

      {!isLoading && !error && filteredProviders.length > 0 && (
        <>
        <div className="grid gap-3">
          {pagedProviders.map((provider) => {
            const isExpanded = expandedProvider === provider.providerId;
            const isSelected = selectedIds.has(provider.providerId);
            const globallyDisabled = !provider.enabled;
            const notImplemented = provider.implemented === false;
            const canToggle = isSelectableSignalSource(provider);

            return (
              <div
                key={provider.providerId}
                className={cn(
                  "rounded-lg border bg-card transition",
                  isSelected && canToggle
                    ? "border-border"
                    : "border-border/50",
                  (globallyDisabled || notImplemented) && "opacity-60",
                )}
              >
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => canToggle && toggleSource(provider.providerId)}
                      disabled={!canToggle || saveMutation.isPending}
                      title={
                        globallyDisabled
                          ? "Disabled in the global provider catalog"
                          : notImplemented
                            ? "No runtime adapter — not selectable"
                          : provider.type === "ats"
                            ? "ATS careers are configured per account on Sources"
                            : isSelected
                              ? "Remove from your signal sources"
                              : "Add to your signal sources"
                      }
                      className={cn(
                        "flex h-6 w-6 items-center justify-center rounded-md border text-xs font-bold",
                        isSelected && canToggle
                          ? "border-brand bg-brand text-brand-foreground"
                          : "border-border text-muted-foreground",
                        !canToggle && "cursor-not-allowed opacity-50",
                      )}
                    >
                      {isSelected && canToggle ? "✓" : ""}
                    </button>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {provider.name}
                      </p>
                      <span
                        className={cn(
                          "inline-block rounded-full px-2 py-0.5 text-[10px] font-medium",
                          TYPE_COLORS[provider.type],
                        )}
                      >
                        {TYPE_LABELS[provider.type]}
                      </span>
                      {notImplemented && (
                        <span className="ml-2 text-[10px] text-muted-foreground">
                          (not implemented)
                        </span>
                      )}
                      {globallyDisabled && (
                        <span className="ml-2 text-[10px] text-muted-foreground">
                          (disabled globally)
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedProvider(
                        isExpanded ? null : provider.providerId,
                      )
                    }
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    {isExpanded ? "Hide" : "Details"}
                  </button>
                </div>
                {isExpanded && (
                  <div className="border-t border-border px-4 py-3 text-xs text-muted-foreground">
                    {provider.description}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <PaginationBar
          page={page}
          pageSize={pageSize}
          totalItems={filteredProviders.length}
          totalPages={totalPages}
          showingFrom={showingFrom}
          showingTo={showingTo}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
        </>
      )}
    </div>
  );
}

export function SourcesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const initialTab: PageTab =
    tabParam === "providers"
      ? "providers"
      : tabParam === "discover"
        ? "discover"
        : "sources";
  const [tab, setTab] = useState<PageTab>(initialTab);
  const { data: profile } = useDiscoveryProfile();
  const enabledCount = enabledCompanyCount(profile);
  const providerCount = enabledProviderCount(profile);

  useEffect(() => {
    if (tabParam === "providers") setTab("providers");
    else if (tabParam === "discover") setTab("discover");
    else if (tabParam === "sources" || !tabParam) setTab("sources");
  }, [tabParam]);

  function selectTab(next: PageTab) {
    setTab(next);
    setSearchParams(next === "sources" ? {} : { tab: next });
  }

  if (tab === "discover") {
    return (
      <>
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">Intake</h1>
          <p className="text-muted-foreground">
            Job discovery via signal scan, AI search, and imports.
          </p>
        </header>
        <div className="mb-4 flex gap-1 border-b border-border">
          {(
            [
              { id: "sources" as const, label: `Sources (${enabledCount})` },
              { id: "providers" as const, label: `Providers (${providerCount})` },
              { id: "discover" as const, label: "Discover jobs" },
            ] as const
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => selectTab(t.id)}
              className={cn(
                "border-b-2 px-4 py-2 text-sm font-medium transition",
                tab === t.id
                  ? "border-brand text-brand"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        <JobDiscoveryPage embedded />
      </>
    );
  }

  return (
    <>
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">Intake</h1>
        <p className="text-muted-foreground">
          Hiring boards, job discovery, and market-signal providers.
        </p>
      </header>

      <div className="mb-6 flex gap-1 border-b border-border">
        {(
          [
            { id: "sources" as const, label: `Sources (${enabledCount})` },
            {
              id: "providers" as const,
              label: `Providers (${providerCount})`,
            },
            { id: "discover" as const, label: "Discover jobs" },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => selectTab(t.id)}
            className={cn(
              "border-b-2 px-4 py-2 text-sm font-medium transition",
              tab === t.id
                ? "border-brand text-brand"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "sources" ? <DiscoveryProfileEditor /> : <ProvidersPanel />}
    </>
  );
}
